import { useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, Check, GripVertical, Plus, X } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { projectDatasetsPath, setProjectDatasets } from "~/services/projects";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

interface ProjectDatasetsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachedIds: string[];
}

export const ProjectDatasetsDialog = ({
  projectId,
  open,
  onOpenChange,
  attachedIds,
}: ProjectDatasetsDialogProps) => {
  const { showSuccess } = useToast();
  const { data, error, isLoading } = useFetcher<Dataset[]>(open ? CATALOG_DATASETS_URL : null);

  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setOrderedIds(attachedIds);
    }
    // Only re-sync from props on open transitions, not on attachedIds reference
    // changes — otherwise SWR revalidation would clobber in-progress drag order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const datasetById = useMemo(() => {
    const map = new Map<string, Dataset>();
    for (const d of data ?? []) {
      map.set(d.id, d);
    }
    return map;
  }, [data]);

  const attachedSet = useMemo(() => new Set(orderedIds), [orderedIds]);

  const attachedDatasets = useMemo(
    () => orderedIds.map((id) => datasetById.get(id)).filter((d): d is Dataset => Boolean(d)),
    [orderedIds, datasetById]
  );

  const toggle = (id: string) => {
    setOrderedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const detach = (id: string) => {
    setOrderedIds((prev) => prev.filter((x) => x !== id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) {
      return;
    }
    setOrderedIds((ids) => {
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) {
        return ids;
      }
      return arrayMove(ids, oldIndex, newIndex);
    });
  };

  const { execute: save, isPending: isSaving } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to update attached datasets",
      onSuccess: async (result) => {
        await mutate(projectDatasetsPath(projectId));
        showSuccess(`${result.length} dataset${result.length === 1 ? "" : "s"} attached`);
        onOpenChange(false);
      },
    }
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Manage datasets"
      description="Add datasets and drag to reorder."
      contentClassName="sm:max-w-[560px]"
      onPrimary={() => void save(orderedIds)}
      isPending={isSaving}
      primaryLabel="Done"
      primaryPendingLabel="Saving…"
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <h3 className="min-w-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Attached ({attachedDatasets.length})
          </h3>
          <Popover modal open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus aria-hidden="true" />
                Add datasets
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="z-[1300] w-[420px] p-0">
              <DatasetPicker
                datasets={data ?? []}
                isLoading={isLoading}
                hasError={Boolean(error)}
                attachedSet={attachedSet}
                onToggle={toggle}
              />
            </PopoverContent>
          </Popover>
        </div>

        {attachedDatasets.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 rounded-md border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm font-medium text-foreground">No datasets attached yet</p>
            <p className="text-xs text-muted-foreground">
              Click <span className="font-medium text-foreground">+ Add datasets</span> to get
              started.
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
              <ul
                className="flex max-h-[50vh] w-full min-w-0 flex-col gap-1 overflow-y-auto"
                role="list"
              >
                {attachedDatasets.map((dataset) => (
                  <SortableAttachedRow
                    key={dataset.id}
                    dataset={dataset}
                    onDetach={() => detach(dataset.id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </FormDialog>
  );
};

interface DatasetPickerProps {
  datasets: Dataset[];
  isLoading: boolean;
  hasError: boolean;
  attachedSet: Set<string>;
  onToggle: (id: string) => void;
}

const DatasetPicker = ({
  datasets,
  isLoading,
  hasError,
  attachedSet,
  onToggle,
}: DatasetPickerProps) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return datasets;
    return datasets.filter(
      (d) => d.title.toLowerCase().includes(q) || (d.description ?? "").toLowerCase().includes(q)
    );
  }, [datasets, search]);

  return (
    <Command>
      <CommandInput
        value={search}
        onValueChange={setSearch}
        placeholder="Search datasets..."
        aria-label="Search datasets"
      />
      <CommandList>
        {isLoading && (
          <div role="status" className="flex flex-col gap-1.5 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}
        {!isLoading && hasError && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-5" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load datasets</p>
          </div>
        )}
        {!isLoading && !hasError && filtered.length === 0 && (
          <CommandEmpty>
            {search ? "No datasets match your search." : "No datasets available."}
          </CommandEmpty>
        )}
        {!isLoading && !hasError && filtered.length > 0 && (
          <div className="p-1">
            {filtered.map((dataset) => {
              const isAttached = attachedSet.has(dataset.id);
              return (
                <CommandItem
                  key={dataset.id}
                  value={dataset.id}
                  onSelect={onToggle}
                  className="flex min-w-0 items-center gap-2 px-2 py-1.5"
                >
                  <DatasetIcon dataset={dataset} size={28} className="shrink-0 rounded-md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{dataset.title}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {dataset.description || "No description"}
                    </p>
                  </div>
                  <Check
                    className={cn(
                      "size-4 shrink-0 text-primary",
                      isAttached ? "opacity-100" : "opacity-0"
                    )}
                    aria-hidden="true"
                  />
                </CommandItem>
              );
            })}
          </div>
        )}
      </CommandList>
    </Command>
  );
};

interface SortableAttachedRowProps {
  dataset: Dataset;
  onDetach: () => void;
}

const SortableAttachedRow = ({ dataset, onDetach }: SortableAttachedRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dataset.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex w-full min-w-0 items-center gap-2 rounded-md border border-border bg-card px-2 py-2",
        isDragging && "shadow-lg ring-2 ring-primary/40"
      )}
    >
      <button
        type="button"
        className="-ml-1 flex size-7 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-accent/40 active:cursor-grabbing"
        aria-label={`Drag to reorder ${dataset.title}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <DatasetIcon dataset={dataset} size={28} className="shrink-0 rounded-md" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{dataset.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          {dataset.description || "No description"}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 text-muted-foreground"
        onClick={onDetach}
        aria-label={`Remove ${dataset.title}`}
      >
        <X className="size-4" />
      </Button>
    </li>
  );
};
